<template>
    <BaseTippy :placement="props.tippyPlacement"
               trigger="click"
               interactive>
        <BaseButton :variant="isGameEndScreen ? ButtonVariantEnum.SECONDARY : ButtonVariantEnum.PRIMARY"
                    :class="{'share-animated-button': isGameEndScreen}"
                    @click="shareValuesUpdate">
            <template v-if="isCertificate">
                <i class="fa-solid fa-share mr-1" />
                <Translate translationKey="quests-dashboard.shareCertificate"
                           fallback="Share Certificate" />
            </template>
            <template v-else-if="isGameEndScreen">
                <i class="fa-solid fa-share mr-1 text-uppercase" />
                <Translate translationKey="general.share"
                           fallback="SHARE" />
            </template>
            <translate v-else
                       translation-key="quests-dashboard.shareIt"
                       fallback="Share It" />
        </BaseButton>
        <template #content>
            <div class="info share-tippy-content"
                 :class="{ 'nft-icons' : isNft }">
                <div class="subtitle">
                    <Translate translationKey="quests-dashboard.shareItNowOn"
                               fallback="Share it NOW on:" />
                </div>
                <div class="share-now d-flex justify-content-center align-items-center">
                    <div class="social-icons d-flex justify-content-center align-items-center">
                        <span class="i-background mr-2"
                              @click="shareTippyStore.share(shareTippyStore.twitterShareLink)">
                            <i class="fa-brands fa-x-twitter" />
                        </span>
                        <span class="i-background mr-2"
                              @click="shareTippyStore.share(shareTippyStore.facebookShareLink)">
                            <i class="fa-brands fa-facebook-f" />
                        </span>
                        <span class="i-background mr-2"
                              @click="shareTippyStore.share(shareTippyStore.linkedInShareLink)">
                            <i class="fa-brands fa-linkedin-in" />
                        </span>
                        <span class="i-background mr-2"
                              @click="shareTippyStore.share(shareTippyStore.telegramShareLink)">
                            <i class="fa-kit fa-telegram" />
                        </span>
                        <span class="i-background"
                              @click="shareTippyStore.copyShareText">
                            <i class="fa-solid fa-copy" />
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </BaseTippy>
</template>

<script setup lang="ts">
    import { TippyOptions } from 'vue-tippy';
    import { onMounted } from 'vue';
    import { useTooltipStore } from '@/js/store/quests/game/modules/TippyMobileStore';
    import { useShareTippyStore } from '@/js/store/quests/dashboard/ShareTippyStore';
    import BaseTippy from '@/js/components/Tippy/BaseTippy.vue';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';

    interface Props {
        shareUrl?: string;
        shareText?: string;
        isCertificate?: boolean;
        isGameEndScreen?: boolean;
        isNft?: boolean;
        tippyPlacement?: TippyOptions['placement'];
    }
    const props = withDefaults(defineProps<Props>(), {
        shareUrl: '',
        shareText: '',
        isCertificate: false,
        isGameEndScreen: false,
        isNft: false,
        tippyPlacement: 'bottom',
    });

    const tooltipStore = useTooltipStore();
    const shareTippyStore = useShareTippyStore();

    const setShareValues = () => {
        shareTippyStore.setShareText(props.shareText);
        shareTippyStore.setShareUrl(props.shareUrl);
        shareTippyStore.setEncodedShareText(encodeURIComponent(`${props.shareText}\n`));
        shareTippyStore.setEncodedShareUrl(encodeURIComponent(props.shareUrl));
        shareTippyStore.setShareLinksUpdate();
    };

    const shareValuesUpdate = () => {
        setShareValues();
        tooltipStore.setShareTooltip(true);
    };

    onMounted(() => {
        setShareValues();
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;

.share-animated-button {
    position: relative;
    overflow: hidden;
    animation: pulsing 1.7s linear infinite;

    @keyframes pulsing {
        0% {
            box-shadow: 0 0 0 rgba(variables.$baseWhite, 0.6);
            transform: scale(1);
        }
        50% {
            box-shadow: 0 0 20px rgba(variables.$baseWhite, 0.8);
            transform: scale(1.05);
        }
        100% {
            box-shadow: 0 0 0 rgba(variables.$baseWhite, 0.6);
            transform: scale(1);
        }
    }
}
</style>

<style lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/design-system/variables" as variables;

.share-tippy-content {
    .subtitle {
        padding-bottom: 16px;
        color: variables.$baseWhite;
        text-align: center;
    }
}

.share-now {
    font-size: 13px;
    font-weight: 500;

    .social-icons {
        i {
            color: variables.$baseWhite;
            font-size: 17px;
        }

        svg {
            width: 17px;
            fill: variables.$baseWhite;
        }
    }

    .i-background {
        display: flex;
        width: 45px;
        height: 45px;
        align-items: center;
        justify-content: center;
        border: 1px solid transparent;
        border-radius: 50px;
        box-shadow: 0 0 8px 0 rgba(180, 180, 180, 0.5);
        text-align: center;
        transition: 0.4s ease-in-out;

        &:nth-child(1) {
            background-color: variables.$baseBlack;
        }

        &:nth-child(2) {
            background-color: legacyVariables.$facebook-color;
        }

        &:nth-child(3) {
            background-color: legacyVariables.$linkedin-color;
        }

        &:nth-child(4) {
            background-color: legacyVariables.$telegram-color;
        }

        &:nth-child(5) {
            background-color: legacyVariables.$purple;
        }

        &:hover {
            border: 1px solid legacyVariables.$muted-lavender;
            cursor: pointer;
            opacity: 0.9;
        }
    }
}

.nft-icons {
    @media (min-width: breakpoints.$lg-breakpoint) and (max-width: breakpoints.$xl-breakpoint) {
        .subtitle {
            padding-bottom: 12px;
        }

        .i-background {
            width: 32px;
            height: 32px;

            i {
                font-size: 14px;
            }
        }
    }
}
</style>
